import { Injectable } from '@angular/core';

import { UserData } from '../interfaces/user-data.interface';
import { UserReadableResource } from 'src/app/api/interfaces/localized-resource.interface';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiCreateSingleResponse } from '../../../api/interfaces/api-create-single-response.interface';
import { HttpClient } from '@angular/common/http';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends AbstractApiV2Service<UserData> implements UserReadableResource {
    public LocalizedResourceName = $localize`user`;
    public LocalizedPluralResourceName = $localize`users`;
    protected resourceName = 'users';

    constructor(http: HttpClient, backendErrorTranslator: BackendErrorTranslatorService) {
        super(http, backendErrorTranslator);

        backendErrorTranslator.addTranslations({
            'Email already used': $localize`Email already used`,
        });
    }

    /**
     * This method will email the user with a link to reset their password.
     * @param email
     */
    public forgotPassword(email: string): Observable<object> {
        const formData = new FormData();
        formData.append('email', email);

        return this.http
            .post(this.getLocation() + '/forgot-password', formData)
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    /**
     * This method will change the user's password.
     * @param token
     * @param newPassword
     */
    public changePassword(token: string, newPassword: string): Observable<object> {
        const formData = new FormData();

        formData.append('token', token);
        formData.append('new_password', newPassword);

        return this.http
            .post(this.getLocation() + '/reset-password', formData)
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    /**
     * This method will email the user with a link to reset their password as an authenticated admin.
     * @param userId
     */
    public resetPassword(userId: string): Observable<object> {
        return this.http
            .post(this.getLocation(userId) + '/reset-password', new FormData())
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public resetMfa(userId: string): Observable<object> {
        return this.http
            .post(this.getLocation(userId) + '/reset-mfa', new FormData())
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public verifyMfa(email: string, code: string): Observable<object> {
        const formData = new FormData();

        formData.append('code', code);
        formData.append('email', email);

        return this.http.post(this.getLocation() + '/verify-mfa', formData).pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public toggleStatus(userId: string): Observable<object> {
        return this.http
            .post(this.getLocation(userId) + '/toggle-status', new FormData())
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public acceptTermsAndConditions(resourceId: string): Observable<object> {
        const formData = new FormData();
        formData.append('accept', 'true');

        return this.http
            .post<ApiCreateSingleResponse>(this.getLocation(resourceId) + '/terms-and-conditions', formData)
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }
}
